import { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, X, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Prompt dismissible que aparece no Dashboard para empresas legadas
 * que ainda nao tem city/state preenchidos. Ajuda a turbinar o badge
 * rolante (geo-social proof) na Landing Page.
 */
export const LocationPrompt = ({ getToken, dm }) => {
  const [visible, setVisible] = useState(false);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/company/settings`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const c = res.data || {};
        // Mostrar apenas se NAO tem geo E NAO foi dispensado
        if (!c.city && !c.state && !c.location_prompt_dismissed) {
          setVisible(true);
        }
      } catch { /* silent */ }
    };
    check();
  }, [getToken]);

  const handleSave = async () => {
    if (!city.trim() && !state.trim()) {
      toast.error('Preencha ao menos a cidade ou UF');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/company/location`,
        { city: city.trim(), state: state.trim(), location_prompt_dismissed: true },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Localização salva! Obrigado por ajudar a comunidade.');
      setVisible(false);
    } catch {
      toast.error('Erro ao salvar localização');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async () => {
    setVisible(false);
    try {
      await axios.put(
        `${API}/company/location/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch { /* silent */ }
  };

  if (!visible) return null;

  return (
    <div
      className={`relative p-4 sm:p-5 rounded-xl border mb-6 overflow-hidden ${
        dm
          ? 'bg-gradient-to-r from-[#C5A059]/10 to-[#0A2647]/40 border-[#C5A059]/20'
          : 'bg-gradient-to-r from-amber-50 to-white border-amber-200'
      }`}
      data-testid="location-prompt"
    >
      <button
        onClick={handleDismiss}
        className={`absolute top-2 right-2 p-1.5 rounded-lg transition-colors ${
          dm ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-100'
        }`}
        aria-label="Dispensar"
        data-testid="location-prompt-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          dm ? 'bg-[#C5A059]/15 text-[#C5A059]' : 'bg-amber-100 text-amber-700'
        }`}>
          <MapPin className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold mb-0.5 ${dm ? 'text-white' : 'text-slate-900'}`}>
            Complete sua localização
          </p>
          <p className={`text-xs leading-relaxed ${dm ? 'text-slate-300/80' : 'text-slate-600'}`}>
            Ajude outros donos a encontrarem referências perto deles. Leva 10 segundos.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <Input
            placeholder="Cidade"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className={`h-10 text-sm w-full sm:w-40 ${dm ? 'bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-400' : ''}`}
            data-testid="location-prompt-city"
          />
          <Input
            placeholder="UF"
            value={state}
            maxLength={2}
            onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
            className={`h-10 text-sm uppercase w-full sm:w-16 ${dm ? 'bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-400' : ''}`}
            data-testid="location-prompt-state"
          />
          <Button
            onClick={handleSave}
            disabled={saving}
            className="h-10 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-semibold text-xs rounded-lg px-4"
            data-testid="location-prompt-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Salvar</>}
          </Button>
        </div>
      </div>
    </div>
  );
};
